import { useCallback, useEffect, useRef } from 'react'
import { ScrollView, StyleProp, View, ViewStyle } from 'react-native'

import { useTour } from '@/components/tour/TourProvider'
import type { SpotMeasurement } from '@/components/tour/types'

/** Margin from the top of the scroll view when scrolling a spot into view */
const SCROLL_TOP_MARGIN = 100

interface TourSpotProps {
  /** Unique identifier — must match a spotId in your TourConfig steps */
  id: string
  children?: React.ReactNode
  /** Style forwarded to the wrapping View */
  style?: StyleProp<ViewStyle>
  /** Set to "none" for measurement-only markers that shouldn't block touches */
  pointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only'
  /** Ref to an ancestor ScrollView — enables auto-scroll when this spot is off-screen */
  scrollRef?: React.RefObject<ScrollView | null>
}

/**
 * Wrap any component with `<TourSpot id="...">` to mark it as a tour target.
 * The spot registers a measurement function so the tour overlay can
 * highlight it when its step is active.
 *
 * Pass `scrollRef` when the spot lives inside a ScrollView so the tour can
 * scroll it into view automatically.
 */
const TourSpot = ({
  id,
  children,
  style,
  pointerEvents,
  scrollRef,
}: TourSpotProps) => {
  const viewRef = useRef<View>(null)
  const { registerSpot, unregisterSpot } = useTour()

  const measure = useCallback(
    (): Promise<SpotMeasurement | null> =>
      new Promise((resolve) => {
        if (!viewRef.current) {
          resolve(null)
          return
        }
        viewRef.current.measureInWindow((x, y, width, height) => {
          if (width > 0 && height > 0) {
            resolve({ x, y, width, height })
          } else {
            resolve(null)
          }
        })
      }),
    []
  )

  const scrollIntoView = useCallback(
    (): Promise<void> =>
      new Promise((resolve) => {
        const scrollView = scrollRef?.current
        const nativeScrollRef = scrollView?.getNativeScrollRef()
        if (!scrollView || !nativeScrollRef || !viewRef.current) {
          resolve()
          return
        }
        // measureLayout gives position relative to the ScrollView's content.
        // The new arch requires a host instance (not the class component ref),
        // so we pass the underlying native scroll view via getNativeScrollRef().
        viewRef.current.measureLayout(
          nativeScrollRef as unknown as View,
          (_left, top) => {
            scrollView.scrollTo({
              animated: true,
              y: Math.max(0, top - SCROLL_TOP_MARGIN),
            })
            resolve()
          },
          () => resolve() // measurement failed — continue without scrolling
        )
      }),
    [scrollRef]
  )

  useEffect(() => {
    registerSpot(id, {
      measure,
      scrollIntoView: scrollRef ? scrollIntoView : undefined,
    })
    return () => unregisterSpot(id)
  }, [id, measure, scrollIntoView, scrollRef, registerSpot, unregisterSpot])

  // collapsable={false} prevents Android from optimizing this View away,
  // which would break measureInWindow.
  return (
    <View
      collapsable={false}
      pointerEvents={pointerEvents}
      ref={viewRef}
      style={style}
    >
      {children}
    </View>
  )
}

export default TourSpot
